import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MiroConnection } from '@shared/services/miroConnect'
import {
  disconnectMiro,
  finishMiroConnection,
  getMiroConnection,
  startMiroConnection,
} from '@shared/services/miroConnect'
import '../connect-miro.css'

type Busy = '' | 'loading' | 'finishing' | 'starting' | 'checking' | 'disconnecting'
type Note = { kind: 'ok' | 'err'; text: string } | null

/**
 * Connect Miro: the one connection between a person's Miro account and
 * WonderWorks. It tells the panel on their boards whose it is, and lets the
 * server place pictures on those boards (2026-09-30: until then those were two
 * separate connections, the panel's made on a page of its own).
 *
 * Asked once. Miro's own page does the asking -- the person chooses the team
 * whose boards they work on and clicks Allow -- and sends them back here with
 * a one-time code, which this page hands to the Worker. The token never comes
 * to the browser. Why the code comes back through this page rather than
 * straight to the Worker: workers/api/src/miroTokens.ts.
 *
 * The panel opens this page when it is not connected, and notices by itself
 * once the connection is made.
 */
export function ConnectMiroPage() {
  const [params, setParams] = useSearchParams()
  const [connection, setConnection] = useState<MiroConnection | null>(null)
  const [busy, setBusy] = useState<Busy>('loading')
  const [note, setNote] = useState<Note>(null)
  /** Back from Miro's page with access just given: the tick comes in with a little pop. */
  const [justConnected, setJustConnected] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const code = params.get('code') ?? ''
    const state = params.get('state') ?? ''
    const refused = params.get('error') ?? ''
    const refusedWhy = params.get('error_description') ?? ''
    // The code is good once; out of the address bar, so a reload cannot
    // try to spend it again.
    if (code || state || refused) setParams({}, { replace: true })

    if (refused) {
      setNote({ kind: 'err', text: `Miro did not give access${refusedWhy ? `: ${refusedWhy}` : '.'}` })
    }

    if (code && state && !refused) {
      setBusy('finishing')
      finishMiroConnection(code, state)
        .then((next) => {
          setConnection(next)
          setJustConnected(next.connected)
        })
        .catch((error: unknown) => {
          setNote({ kind: 'err', text: error instanceof Error ? error.message : 'Could not connect.' })
          return getMiroConnection().then(setConnection)
        })
        .catch(() => setConnection(null))
        .finally(() => setBusy(''))
      return
    }

    getMiroConnection()
      .then(setConnection)
      .catch((error: unknown) =>
        setNote({ kind: 'err', text: error instanceof Error ? error.message : 'Could not read the connection.' }),
      )
      .finally(() => setBusy(''))
  }, [params, setParams])

  async function handleConnect() {
    setBusy('starting')
    setNote(null)
    try {
      window.location.assign(await startMiroConnection())
    } catch (error) {
      setNote({ kind: 'err', text: error instanceof Error ? error.message : 'Could not start.' })
      setBusy('')
    }
  }

  async function handleCheck() {
    setBusy('checking')
    setNote(null)
    try {
      const next = await getMiroConnection(true)
      setConnection(next)
      const check = next.check
      setNote(
        check?.ok
          ? {
              kind: 'ok',
              text: `It works: Miro answers as ${check.userName || 'you'}${check.teamName ? ` in ${check.teamName}` : ''}.`,
            }
          : { kind: 'err', text: check?.error || 'Miro did not accept the access.' },
      )
    } catch (error) {
      setNote({ kind: 'err', text: error instanceof Error ? error.message : 'Could not check.' })
    } finally {
      setBusy('')
    }
  }

  async function handleDisconnect() {
    setBusy('disconnecting')
    setNote(null)
    setJustConnected(false)
    try {
      setConnection(await disconnectMiro())
      setNote({
        kind: 'ok',
        text: 'Disconnected. WonderWorks no longer has access to your boards, and the panel will ask to be connected again.',
      })
    } catch (error) {
      setNote({ kind: 'err', text: error instanceof Error ? error.message : 'Could not disconnect.' })
    } finally {
      setBusy('')
    }
  }

  /*
   * One page, three states (2026-10-21). It answers two questions, in this
   * order: where am I (the big line and the three steps), and what do I do
   * now (one card, one button). Connected, the next thing is always "go back
   * to Miro"; checking, reconnecting and disconnecting sit quietly below for
   * the odd day they are needed.
   */
  const reading = busy === 'loading' || busy === 'finishing'
  const state: 'reading' | 'none' | 'connected' | 'broken' = reading
    ? 'reading'
    : connection?.needsReconnect
      ? 'broken'
      : connection?.connected
        ? 'connected'
        : 'none'
  const since = connection?.connectedAt
    ? new Date(connection.connectedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : ''
  const team = connection?.teamName ?? ''
  const steps: Array<'' | 'done' | 'now' | 'bad'> =
    state === 'connected' ? ['done', 'now'] : state === 'broken' ? ['bad', ''] : ['now', '']
  // Two steps (2026-10-21, second version): connect here, then back to the
  // Miro tab this page was opened from. The panel there notices by itself.
  const stepNames = ['Connect Miro', 'Back to Miro']

  const title =
    state === 'reading'
      ? busy === 'finishing'
        ? 'Finishing the connection…'
        : 'Checking your connection…'
      : state === 'connected'
        ? 'You’re connected'
        : state === 'broken'
          ? 'Your Miro connection stopped working'
          : 'Miro isn’t connected yet'
  const sub =
    state === 'connected'
      ? team
        ? <>WonderWorks is {justConnected ? 'now ' : ''}connected to your Miro team <strong>{team}</strong>.</>
        : <>WonderWorks is {justConnected ? 'now ' : ''}connected to your Miro account.</>
      : state === 'broken'
        ? 'Miro no longer accepts the access given before. This happens if it was removed in Miro.'
        : state === 'none'
          ? 'Connect once, and the WonderWorks panel in Miro will know it’s you.'
          : ''

  return (
    <section className="cmiro">
      <span className="cmiro__eyebrow">Miro connection</span>

      <div className="cmiro__hero">
        <span className={`cmiro__badge cmiro__badge--${state}${justConnected ? ' cmiro__badge--pop' : ''}`} aria-hidden="true">
          {state === 'connected' ? (
            <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          ) : state === 'broken' ? (
            <svg viewBox="0 0 24 24"><path d="M12 6v8M12 18.5v.01" /></svg>
          ) : state === 'none' ? (
            <svg viewBox="0 0 24 24"><path d="M9 7V3M15 7V3M7 7h10v4a5 5 0 0 1-10 0V7ZM12 16v5" /></svg>
          ) : (
            <span className="cmiro__spinner" />
          )}
        </span>
        <div>
          <h1 className="cmiro__title">{title}</h1>
          {sub ? <p className="cmiro__sub">{sub}</p> : null}
        </div>
      </div>

      {state !== 'reading' ? (
        <>
          <ol className="cmiro__steps" aria-label="Where you are">
            {stepNames.map((name, i) => (
              <li key={name} className={`cmiro__step${steps[i] ? ` cmiro__step--${steps[i]}` : ''}`} aria-current={steps[i] === 'now' ? 'step' : undefined}>
                <b>Step {i + 1}</b>
                {name}
              </li>
            ))}
          </ol>

          <div className="cmiro__next">
            <span className="cmiro__label">Next</span>
            {state === 'connected' ? (
              <>
                <h2 className="cmiro__next-title">Go back to Miro</h2>
                <p className="cmiro__close">
                  <strong>Close this tab</strong> and go back to the Miro board you came from. The WonderWorks panel there
                  picks this up by itself; nothing else to set up.
                </p>
              </>
            ) : (
              <>
                <h2 className="cmiro__next-title">{state === 'broken' ? 'Connect again' : 'Connect your Miro account'}</h2>
                <p className="cmiro__text">
                  {state === 'broken'
                    ? 'Same as the first time: Miro asks you to allow WonderWorks, and you’re back.'
                    : 'On Miro’s page, choose the team whose boards you work on, then Allow. It takes a few seconds and you only do it once.'}
                </p>
                <div className="cmiro__row">
                  <button type="button" className="ww-btn ww-btn--primary" onClick={() => void handleConnect()} disabled={busy !== ''}>
                    {busy === 'starting' ? 'Opening Miro…' : state === 'broken' ? 'Connect again' : 'Connect to Miro'}
                  </button>
                </div>
              </>
            )}
          </div>

          {state === 'connected' && connection?.otherAccount ? (
            <p className="cmiro__warn">
              This is a different Miro account from the one linked to your WonderWorks panel. Pictures can only reach
              boards this account can open.
            </p>
          ) : null}

          {note ? (
            <p className={`cmiro__note cmiro__note--${note.kind}`} role={note.kind === 'err' ? 'alert' : 'status'}>
              {note.text}
            </p>
          ) : null}

          {state === 'connected' ? (
            <div className="cmiro__foot">
              <span>
                {team ? <>Team <strong>{team}</strong></> : 'Connected'}
                {since ? ` · since ${since}` : ''}
              </span>
              <span className="cmiro__links">
                <button type="button" onClick={() => void handleCheck()} disabled={busy !== ''}>
                  {busy === 'checking' ? 'Checking…' : 'Check it works'}
                </button>
                <button type="button" onClick={() => void handleConnect()} disabled={busy !== ''}>
                  {busy === 'starting' ? 'Opening Miro…' : 'Reconnect'}
                </button>
                <button type="button" className="cmiro__danger" onClick={() => void handleDisconnect()} disabled={busy !== ''}>
                  {busy === 'disconnecting' ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </span>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
